'use client';

import { useState, useEffect } from 'react';
import { useOnchainPay, useOnchainWallet } from '@onchainfi/connect';
import { X, CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';
import type { PaymentLinkProps } from '../types';
import { validatePaymentData } from '../utils/encoding';
import { HYPERLINK_VERSION } from '../version';

/**
 * PaymentLink - A text link that triggers an inline payment
 * 
 * @example
 * <p>
 *   Enjoyed this? 
 *   <PaymentLink to="0x..." amount="0.50">
 *     Buy me a coffee ☕
 *   </PaymentLink>
 * </p>
 */
export function PaymentLink({
  to,
  amount,
  network = 'base',
  token = 'USDC',
  memo: _memo,
  priority = 'balanced',
  children,
  className,
  onSuccess,
  onError,
  confirmMessage: _confirmMessage,
  skipConfirm = true,  // Default to skip for smooth UX
  disabled = false,
}: PaymentLinkProps) {
  const { isConnected, login } = useOnchainWallet();
  const { pay, isPaying } = useOnchainPay();
  
  const [showModal, setShowModal] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState<string>();
  const [txHash, setTxHash] = useState<string>();
  const [pendingPayment, setPendingPayment] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);

  // Debug logging on mount
  useEffect(() => {
    console.log(`🔗 Hyperlink PaymentLink v${HYPERLINK_VERSION} mounted`, { to, amount, network, token });
  }, []);

  // Auto-execute payment after wallet connection
  useEffect(() => {
    if (isConnected && pendingPayment) {
      setPendingPayment(false);
      executePayment();
    }
  }, [isConnected, pendingPayment]);

  // Debug payment success state
  useEffect(() => {
    if (paymentSuccess) {
      console.log('✅ Hyperlink: Payment success state set', { 
        paymentSuccess, 
        txHash,
        shouldShowToast: true 
      });
    }
  }, [paymentSuccess, txHash]);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (disabled || isPaying) return;
    
    // Validate payment data
    const validation = validatePaymentData({ to, amount, network, token });
    if (!validation.valid) {
      setPaymentError(validation.error);
      setShowModal(true);
      return;
    }
    
    // If not connected, trigger wallet connection
    if (!isConnected) {
      setPendingPayment(true);  // Mark that we want to pay after connection
      login();  // Open Connect wallet modal
      return;
    }
    
    // Connected - execute payment directly (or show confirmation if skipConfirm=false)
    if (skipConfirm) {
      executePayment();
    } else {
      setShowModal(true);
    }
  };

  const executePayment = async () => {
    setPaymentSuccess(false);
    setPaymentError(undefined);
    setTxHash(undefined);

    await pay({
      to,
      amount,
      network,
      priority,
      onSuccess: (hash) => {
        console.log('✅ Hyperlink: Payment successful, setting state', { hash });
        setPaymentSuccess(true);
        setTxHash(hash);
        console.log('✅ Hyperlink: State updated, toast should appear now');
        onSuccess?.(hash);
        
        // Auto-dismiss toast after 5 seconds with fade out
        setTimeout(() => {
          setIsFadingOut(true);
          setTimeout(() => {
            setPaymentSuccess(false);
            setTxHash(undefined);
            setIsFadingOut(false);
          }, 300); // Wait for fade animation to complete
        }, 5000);
      },
      onError: (error) => {
        setPaymentError(error.message);
        onError?.(error);
      },
    });
  };

  const closeModal = () => {
    setShowModal(false);
    setPaymentSuccess(false);
    setPaymentError(undefined);
    setTxHash(undefined);
  };

  const defaultClassName = 'text-purple-400 hover:text-purple-300 underline cursor-pointer transition-colors';

  return (
    <>
      <span
        onClick={handleClick}
        className={className || defaultClassName}
        style={{ 
          cursor: (disabled || isPaying) ? 'not-allowed' : 'pointer',
          opacity: (disabled || isPaying) ? 0.5 : 1,
        }}
      >
        {children}
      </span>

      {/* Success/Error Modal - Only shown after payment */}
      {showModal && (paymentSuccess || paymentError) && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          onClick={closeModal}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

          {/* Modal */}
          <div
            className="relative bg-black/90 backdrop-blur-md border border-purple-500/30 rounded-2xl p-8 shadow-2xl shadow-purple-500/20 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-600">
                {paymentSuccess ? 'Payment Successful!' : 'Payment Failed'}
              </h2>
              <button
                onClick={closeModal}
                className="text-purple-400 hover:text-purple-300 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Success State */}
            {paymentSuccess && (
              <div className="text-center py-4">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-full mb-4">
                  <CheckCircle className="text-green-400" size={32} />
                </div>
                <p className="text-green-200 mb-4">
                  Payment of {amount} {token} sent successfully!
                </p>
                {txHash && (
                  <a
                    href={`https://basescan.org/tx/${txHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-purple-400 hover:text-purple-300 flex items-center justify-center gap-1"
                  >
                    View transaction <ExternalLink size={14} />
                  </a>
                )}
              </div>
            )}

            {/* Error State */}
            {paymentError && !paymentSuccess && (
              <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="text-red-400 flex-shrink-0" size={24} />
                  <div>
                    <p className="text-red-200 font-semibold text-sm">Error</p>
                    <p className="text-red-200/80 text-xs mt-1">{paymentError}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Close Button */}
            <button
              onClick={closeModal}
              className="w-full mt-4 bg-purple-900/30 hover:bg-purple-900/50 border border-purple-500/30 hover:border-purple-400/50 rounded-lg py-3 text-purple-300 font-medium transition-all"
            >
              Close
            </button>

            {/* 
            ==========================================
            CONFIRMATION MODAL - Commented out for smooth UX
            ==========================================
            
            To re-enable:
            1. Change skipConfirm default to false (line 32)
            2. Uncomment this section
            3. Change line 125 condition to just: {showModal && (
            
            {!paymentSuccess && !paymentError && (
              <>
                <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4 mb-6 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-purple-300/80 text-sm">Amount:</span>
                    <span className="text-purple-100 font-semibold">{amount} {token}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-purple-300/80 text-sm">To:</span>
                    <span className="text-purple-100 text-xs font-mono">
                      {to.slice(0, 6)}...{to.slice(-4)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-purple-300/80 text-sm">Network:</span>
                    <span className="text-purple-100 text-xs capitalize">{network}</span>
                  </div>
                  {memo && (
                    <div className="flex justify-between">
                      <span className="text-purple-300/80 text-sm">Memo:</span>
                      <span className="text-purple-100 text-xs">{memo}</span>
                    </div>
                  )}
                </div>

                {confirmMessage && (
                  <p className="text-purple-200/80 text-sm mb-6 text-center">
                    {confirmMessage}
                  </p>
                )}

                <button
                  onClick={executePayment}
                  disabled={isPaying}
                  className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 disabled:from-purple-900/50 disabled:to-purple-800/50 text-white font-semibold py-4 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:cursor-not-allowed"
                >
                  {isPaying ? (
                    <>
                      <Loader2 className="animate-spin" size={20} />
                      Processing...
                    </>
                  ) : (
                    `Confirm Payment (${amount} ${token})`
                  )}
                </button>
              </>
            )}
            */}
          </div>
        </div>
      )}

      {/* Toast Notification - Bottom Right (Pure inline styles, no Tailwind) */}
      {paymentSuccess && (
        <div 
          style={{ 
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            zIndex: 10001,
            animation: 'slideInUp 0.4s ease-out',
            opacity: isFadingOut ? 0 : 1,
            transform: isFadingOut ? 'translateY(1rem)' : 'translateY(0)',
            transition: 'opacity 0.3s ease-out, transform 0.3s ease-out',
          }}
        >
          <style>{`
            @keyframes slideInUp {
              from {
                transform: translateY(100%);
                opacity: 0;
              }
              to {
                transform: translateY(0);
                opacity: 1;
              }
            }
          `}</style>
          <div 
            style={{
              backgroundColor: 'rgba(20, 83, 45, 0.95)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(34, 197, 94, 0.5)',
              borderRadius: '0.5rem',
              padding: '1rem',
              minWidth: '320px',
              boxShadow: '0 20px 25px -5px rgba(34, 197, 94, 0.3), 0 8px 10px -6px rgba(34, 197, 94, 0.2)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
              <div style={{ flexShrink: 0, marginTop: '0.125rem' }}>
                <CheckCircle color="#4ade80" size={20} />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ color: '#f0fdf4', fontWeight: 600, fontSize: '0.875rem', margin: 0 }}>
                  Payment Successful!
                </p>
                <p style={{ color: 'rgba(187, 247, 208, 0.8)', fontSize: '0.75rem', marginTop: '0.25rem', marginBottom: 0 }}>
                  {amount} {token} sent
                </p>
                {txHash && (
                  <a
                    href={`https://basescan.org/tx/${txHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: '0.75rem',
                      color: '#86efac',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      marginTop: '0.5rem',
                      textDecoration: 'none',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#bbf7d0'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#86efac'}
                  >
                    View on BaseScan <ExternalLink size={12} />
                  </a>
                )}
              </div>
              <button
                onClick={() => {
                  setIsFadingOut(true);
                  setTimeout(() => {
                    setPaymentSuccess(false);
                    setTxHash(undefined);
                    setIsFadingOut(false);
                  }, 300);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(134, 239, 172, 0.6)',
                  fontSize: '1.125rem',
                  lineHeight: 1,
                  cursor: 'pointer',
                  padding: 0,
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#bbf7d0'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(134, 239, 172, 0.6)'}
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

